import React, { useState, useEffect } from 'react';
import '../styles/QuizTopicSelect.scss';
import { track } from '@vercel/analytics';

function QuizTopicSelect({ 
  API_BASE_URL, 
  authToken, 
  subject, 
  onTopicsSelected, 
  onBack,
  onShareIdEntered
}) {
  const [topics, setTopics] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [questionCount, setQuestionCount] = useState(10);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [error, setError] = useState(null);
  const [shareIdInput, setShareIdInput] = useState('');
  const [shareIdError, setShareIdError] = useState(null);
  const [loadingShareId, setLoadingShareId] = useState(false);
  const [isGeneratingQuiz, setIsGeneratingQuiz] = useState(false);

  // Fetch available topics for the selected subject
  useEffect(() => {
    async function fetchTopics() {
      try {
        setLoadingTopics(true);
        setError(null); // Clear previous errors
        const response = await fetch(
          `${API_BASE_URL}/api/quiz/topics/${subject}`,
          {
            headers: {
              'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch topics: ${response.status}`);
        }

        const data = await response.json();
        setTopics(data.topics || []);
        
        // Track successful topics fetch
        track('quiz_topics_fetch_success', {
          subject: subject,
          topic_count: (data.topics || []).length
        });
      } catch (err) {
        console.error('Error fetching topics:', err);
        setError(err.message);
        
        // Track topics fetch error
        track('quiz_topics_fetch_error', {
          subject: subject,
          error: err.message
        });
      } finally {
        setLoadingTopics(false);
      }
    }

    fetchTopics();
  }, [API_BASE_URL, authToken, subject]);

  // Handle checkbox change
  const handleTopicChange = (topic) => {
    setSelectedTopics(prev => {
      if (prev.includes(topic)) {
        return prev.filter(t => t !== topic);
      } else {
        return [...prev, topic];
      }
    });
  };

  // Handle submitting the selected topics
  const handleSubmit = async () => {
    setIsGeneratingQuiz(true); // Start loading
    
    // Track quiz generation initiation
    track('quiz_generation_initiated', {
      subject: subject,
      selected_topics: selectedTopics,
      topic_count: selectedTopics.length,
      question_count: questionCount
    });
    
    try {
      await onTopicsSelected(selectedTopics, questionCount);
    } catch (err) {
      console.error("Error during quiz generation in QuizTopicSelect:", err);
      // Optionally set an error state here to display to the user
    } finally {
      setIsGeneratingQuiz(false); // Stop loading
    }
  };

  // Handle loading quiz by Share ID
  const handleLoadShareId = async () => {
    if (shareIdInput.length !== 5) {
      setShareIdError('Share ID must be 5 characters long.');
      return;
    }
    setShareIdError(null);
    setLoadingShareId(true);
    
    // Track share ID load attempt
    track('quiz_share_id_load_attempted', {
      subject: subject,
      share_id: shareIdInput.toUpperCase()
    });
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/quiz/by-share-id/${shareIdInput.toUpperCase()}`,
        {
          headers: {
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
          }
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to load quiz by share ID: ${response.status}`);
      }
      const data = await response.json();
      if (data && data.id) {
        // Track successful share ID load
        track('quiz_share_id_load_success', {
          subject: subject,
          share_id: shareIdInput.toUpperCase(),
          quiz_id: data.id
        });
        
        onShareIdEntered(data.id); // Call the new callback with the actual quiz ID
      } else {
        throw new Error('Invalid response from server when loading by share ID.');
      }
    } catch (err) {
      console.error('Error loading quiz by Share ID:', err);
      setShareIdError(err.message || 'Failed to load quiz. Please check the ID and try again.');
      
      // Track share ID load error
      track('quiz_share_id_load_error', {
        subject: subject,
        share_id: shareIdInput.toUpperCase(),
        error: err.message
      });
    } finally {
      setLoadingShareId(false);
    }
  };

  // Check if all topics are selected
  const allTopicsSelected = topics.length > 0 && selectedTopics.length === topics.length;

  // Toggle all topics
  const toggleAllTopics = () => {
    if (allTopicsSelected) {
      setSelectedTopics([]);
    } else {
      setSelectedTopics([...topics]);
    }
  };

  return (
    <div className="quiz-topic-select">
      <button onClick={onBack} className="back-button" disabled={isGeneratingQuiz}>
        ← Back to Subjects
      </button>
      
      <h2>{subject} Quiz Setup</h2>
      
      {/* Section for Share ID input */}
      <div className="share-id-section">
        <h3>Or Enter Quiz Share ID</h3>
        <div className="share-id-input-group">
          <input 
            type="text" 
            value={shareIdInput}
            onChange={(e) => setShareIdInput(e.target.value.toUpperCase())}
            placeholder="e.g. ABC12"
            maxLength={5}
            className="share-id-input"
            disabled={loadingShareId || isGeneratingQuiz}
          />
          <button 
            onClick={handleLoadShareId} 
            className="load-share-id-button"
            disabled={loadingShareId || shareIdInput.length !== 5 || isGeneratingQuiz}
          >
            {loadingShareId ? 'Loading...' : 'Load Quiz'}
          </button>
        </div>
        {shareIdError && <p className="error-message share-id-error">{shareIdError}</p>}
      </div>

      <hr className="section-divider" />

      {loadingTopics ? (
        <div className="loading">Loading topics...</div>
      ) : error ? (
        <div className="error-message">Error: {error}</div>
      ) : (
        <>
          <h3>Create New Quiz</h3>
          <div className="question-count-selector">
            <label htmlFor="question-count">Number of Questions:</label>
            <select 
              id="question-count" 
              value={questionCount} 
              onChange={(e) => setQuestionCount(parseInt(e.target.value))}
              disabled={isGeneratingQuiz}
            >
              <option value={5}>5 Questions</option>
              <option value={10}>10 Questions</option>
              <option value={15}>15 Questions</option>
              <option value={20}>20 Questions</option>
            </select>
          </div>

          <div className="topics-section">
            <h4>Select Topics to Include</h4>
            
            {topics.length === 0 ? (
              <p>No topics available for this subject.</p>
            ) : (
              <>
                <div className="select-all-option">
                  <label>
                    <input 
                      type="checkbox" 
                      checked={allTopicsSelected} 
                      onChange={toggleAllTopics} 
                      disabled={isGeneratingQuiz}
                    />
                    {allTopicsSelected ? 'Deselect All' : 'Select All Topics'}
                  </label>
                </div>
                
                <div className="topics-list">
                  {topics.map(topic => (
                    <div key={topic} className="topic-checkbox">
                      <label>
                        <input 
                          type="checkbox" 
                          checked={selectedTopics.includes(topic)} 
                          onChange={() => handleTopicChange(topic)} 
                          disabled={isGeneratingQuiz}
                        />
                        {topic}
                      </label>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="action-buttons">
            <button 
              className="generate-button"
              onClick={handleSubmit}
              disabled={selectedTopics.length === 0 || isGeneratingQuiz}
            >
              {isGeneratingQuiz ? (
                <>
                  <span className="spinner" /> Generating...
                </>
              ) : (
                'Generate Quiz'
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default QuizTopicSelect; 